import type {
  XscpData,
  XscpLibraryComponent,
  XscpLibraryCopyTree,
  XscpLibraryCopyVariant,
} from '@packages/systems/core/records/Xscp';
import type {JSONNode} from '@packages/shared/types/JSONNode';

export function readReferences(data: XscpData): XscpLibraryComponent[] | null {
  const references = data.payload.libraryComponents;
  if (
    data.payload.expandUserComponents !== false ||
    !Array.isArray(references) ||
    references.length === 0 ||
    !Array.isArray(data.payload.nodes)
  ) {
    return null;
  }
  const ids = new Set<string>();
  for (const reference of references) {
    if (
      !reference ||
      typeof reference.id !== 'string' ||
      !reference.id ||
      ids.has(reference.id) ||
      typeof reference.profileId !== 'string' ||
      !reference.profileId ||
      !Array.isArray(reference.origin) ||
      reference.origin.length !== 2 ||
      !reference.origin.every(
        (part) => typeof part === 'string' && part.length > 0
      )
    ) {
      return null;
    }
    ids.add(reference.id);
  }
  return references;
}

// Resolve each instance separately, including shared components inside an unshared parent.
// Expansions are already prepared; this assembles the chosen fragments without unlinking again.
// Copy passes () => false for the fully expanded fallback. Paste decides what can stay linked.
export function resolveLibraryCopyTree(
  variant: XscpLibraryCopyTree,
  canRetain: (reference: XscpLibraryComponent) => boolean
): XscpData {
  if (
    !variant?.expandedInstances ||
    typeof variant.expandedInstances !== 'object'
  )
    throw new Error('Invalid library variants');
  // All instances of one source component share a retention decision, across branches.
  const decisions = new Map<
    string,
    {reference: XscpLibraryComponent; retain: boolean}
  >();
  // Collect references only for components that remain linked in the selected result.
  const references = new Map<string, XscpLibraryComponent>();
  // Track the active recursion path: reusing a completed expansion is valid, a cycle is not.
  const resolving = new Set<XscpLibraryCopyVariant>();
  const resolve = (branch: XscpLibraryCopyVariant): XscpData => {
    if (!branch?.data?.payload || !branch.expandedInstanceIds) {
      throw new Error('Invalid library copy tree');
    }
    if (resolving.has(branch)) throw new Error('Cyclic library variants');
    resolving.add(branch);
    const data = branch.data;
    if (!Array.isArray(data.payload.nodes)) {
      throw new Error('Invalid library nodes');
    }
    const branchReferences = data.payload.libraryComponents
      ? readReferences(data)
      : [];
    if (!branchReferences) throw new Error('Invalid library references');
    // Library references use component IDs; the node index below uses element/instance IDs.
    const byId = new Map(branchReferences.map((ref) => [ref.id, ref]));
    // Selected expansions contribute resources and preparation counts as well as nodes.
    const additions: XscpData[] = [];
    const nodes: JSONNode[] = [];
    const nodesById = new Map(
      data.payload.nodes.map((node) => [node._id, node])
    );
    if (nodesById.size !== data.payload.nodes.length)
      throw new Error('Duplicate node ID');
    // Distinguish an ancestor cycle from a node reached again through another reference.
    const visiting = new Set<string>();
    const visited = new Set<string>();
    const visit = (id: string) => {
      if (visiting.has(id)) throw new Error('Cyclic library nodes');
      if (visited.has(id)) return;
      const node = nodesById.get(id);
      if (!node) throw new Error('Missing library node');
      visiting.add(id);
      visited.add(id);
      if (node.type === 'Symbol') {
        const ref = byId.get(node.data?.sym?.inst);
        if (!ref) throw new Error('Missing library reference');
        let decision = decisions.get(ref.id);
        if (decision) {
          // A cached decision is valid only if this ID still identifies the same library component.
          const previous = decision.reference;
          if (
            previous.profileId !== ref.profileId ||
            previous.origin[0] !== ref.origin[0] ||
            previous.origin[1] !== ref.origin[1]
          )
            throw new Error('Conflicting library references');
        } else {
          decision = {reference: ref, retain: canRetain(ref)};
          decisions.set(ref.id, decision);
        }
        if (decision.retain) {
          references.set(ref.id, ref);
          nodes.push(node);
        } else {
          // Each branch selects from the shared expansion table. The same instance ID can
          // need different expansions after its parent's styles or slot content change.
          const variantId = branch.expandedInstanceIds[node._id];
          if (
            typeof variantId !== 'string' ||
            !Object.prototype.hasOwnProperty.call(
              variant.expandedInstances,
              variantId
            )
          )
            throw new Error('Missing library variant');
          // An expanded parent can still contain linked children that need their own decision.
          const expandedInstance = resolve(
            variant.expandedInstances[variantId]!
          );
          const [root, ...children] = expandedInstance.payload.nodes;
          if (!root) throw new Error('Empty expanded library instance');
          // Keep the ID already referenced by the containing parent or slot.
          nodes.push({...root, _id: node._id}, ...children);
          additions.push(expandedInstance);
          visiting.delete(id);
          // The expansion supplies its own slot subtree; do not also append the original one.
          return;
        }
      } else nodes.push(node);
      for (const child of node.children ?? []) visit(child);
      // Retaining a component does not retain everything in its slots. Serialized slot
      // nodes live in override arrays, so they also need traversal and retention decisions.
      for (const value of Object.values(node.data?.sym?.overrides ?? {})) {
        if (Array.isArray(value)) {
          for (const child of value) {
            if (typeof child === 'string' && nodesById.has(child)) visit(child);
          }
        }
      }
      visiting.delete(id);
    };
    // The serializer places the root first. Only nodes reachable from it belong to this branch.
    const root = data.payload.nodes[0];
    if (!root) throw new Error('Empty library copy');
    visit(root._id);
    // Merge resources from selected fragments, not every alternative in expandedInstances.
    const payloads = [data, ...additions].map((part) => part.payload);
    const styles = new Map<string, XscpData['payload']['styles'][number]>();
    // Each expanded instance carries its own snapshot of shared styles.
    for (const payload of payloads) {
      // Merge child links so later snapshots don't orphan earlier combo classes.
      for (const style of payload.styles) {
        const previous = styles.get(style._id);
        styles.set(
          style._id,
          previous
            ? {
                ...style,
                children: [
                  ...new Set([...previous.children, ...style.children]),
                ],
              }
            : style
        );
      }
    }
    // Assets and interaction records can appear in several fragments; the last copy wins by ID.
    const unique = <T>(items: T[], key: (item: T) => string): T[] => [
      ...new Map(items.map((item) => [key(item), item])).values(),
    ];
    // Sum work performed by selected expansions, preserve the source site, and OR the flag.
    const meta = {...data.meta};
    for (const addition of additions) {
      for (const key of Object.keys(meta) as (keyof typeof meta)[]) {
        if (key === 'sourceSiteId') continue;
        if (key === 'richTextComponentsStripped')
          meta[key] ||= addition.meta[key];
        else meta[key] += addition.meta[key];
      }
    }
    resolving.delete(branch);
    return {
      ...data,
      meta,
      payload: {
        nodes,
        styles: [...styles.values()],
        assets: unique(
          payloads.flatMap((p) => p.assets),
          (item) => item._id
        ),
        ix1: unique(
          payloads.flatMap((p) => p.ix1),
          (item) => item._id
        ),
        ix2: {
          events: unique(
            payloads.flatMap((p) => p.ix2.events),
            (item) => item.id
          ),
          interactions: unique(
            payloads.flatMap((p) => p.ix2.interactions),
            (item) => item.id
          ),
          actionLists: unique(
            payloads.flatMap((p) => p.ix2.actionLists),
            (item) => item.id
          ),
        },
        ix3: {
          interactions: unique(
            payloads.flatMap((p) => p.ix3?.interactions ?? []),
            (item) => item.id
          ),
          timelines: unique(
            payloads.flatMap((p) => p.ix3?.timelines ?? []),
            (item) => item.id
          ),
        },
        // Paste can consume linked instances here. Copy marks its all-expanded result true.
        expandUserComponents: false,
      },
    };
  };
  const selected = resolve(variant);
  // Replacing a Symbol also replaces its slot subtree. Discard the old slot nodes.
  const byId = new Map(selected.payload.nodes.map((node) => [node._id, node]));
  // Individually valid fragments can still introduce duplicate IDs when combined.
  if (byId.size !== selected.payload.nodes.length)
    throw new Error('Duplicate node ID');
  // Validate reachability and cycles in the assembled graph, across fragment boundaries.
  const active = new Set<string>();
  const visited = new Set<string>();
  const nodes: JSONNode[] = [];
  const visit = (id: string) => {
    if (active.has(id)) throw new Error('Cyclic library nodes');
    if (visited.has(id)) return;
    const node = byId.get(id);
    if (!node) throw new Error('Missing library node');
    active.add(id);
    visited.add(id);
    nodes.push(node);
    for (const child of node.children ?? []) visit(child);
    for (const value of Object.values(node.data?.sym?.overrides ?? {})) {
      if (Array.isArray(value)) {
        for (const child of value) {
          if (typeof child === 'string' && byId.has(child)) visit(child);
        }
      }
    }
    active.delete(id);
  };
  const root = selected.payload.nodes[0];
  if (!root) throw new Error('Empty library copy');
  visit(root._id);
  // The destination only needs library metadata for Symbols that survive in the final tree.
  const used = new Set(
    nodes
      .filter((node) => node.type === 'Symbol')
      .map((node) => node.data?.sym?.inst)
  );
  return {
    ...selected,
    payload: {
      ...selected.payload,
      nodes,
      libraryComponents: [...references.values()].filter((ref) =>
        used.has(ref.id)
      ),
    },
  };
}
